// askSmart-ui/src/pages/TOSP/portal/tenantExpenses.ts
//
// What a tenant is invoiced each month, worked out from the lease rather than
// stored. Every figure here is derived from records the admin already keeps —
// the lease's rents, the roll's tax budget, Configuration's BMVA rates and the
// sales the tenant reported — so a term corrected on the management side moves
// the statement without anything being republished.
import {
  bmvaMonthlyFee,
  bmvaRoyalty,
  BudgetStatementSnapshot,
  BudgetYear,
  LeasedSpace,
  Occupancy,
  occupancyDayShare,
  occupancyEndDate,
  rentPsfOn,
  rollPartsForUnit,
  spaceEscalation,
  TaxRollRecord,
  Tenant,
  TospConfiguration,
  Unit,
  unitBudgetTotal,
} from "../types";
import {
  calculateCamRecovery,
  effectiveLines,
  withManagementFee,
} from "../budgetAllocation";
import { tenantBreakpointDetail } from "./portalSummary";
import { TenantSalesMonth } from "./tenantSales";

/**
 * Ontario's HST, as a fraction.
 *
 * A constant rather than a Configuration field: it is one rate set by statute
 * for the whole province, and a per-org copy of it is a per-org chance to be
 * wrong about it. Move it to Configuration the day the portfolio crosses a
 * provincial border.
 */
export const HST_RATE = 0.13;

/** A space that carries no percentage rent and no BMVA fee — the lockers. */
const STORAGE_SPACE = /storage|locker/i;

const pad = (n: number): string => String(n).padStart(2, "0");
const monthStart = (year: number, month: number): string => `${year}-${pad(month)}-01`;
const monthEnd = (year: number, month: number): string =>
  new Date(year, month, 0).toISOString().slice(0, 10);

/** Twelve nulls — a line nobody has been able to fill in. */
const emptyMonths = (): (number | null)[] => Array(12).fill(null);

export interface ExpenseLine {
  key: string;
  label: string;
  /** Twelve entries, January first. Null where there is nothing to charge. */
  months: (number | null)[];
  total: number | null;
  /** The invoice total, set in bold. */
  emphasis?: boolean;
}

/**
 * The headline rates: what the year costs per square foot held.
 *
 * The figure a tenant checks against other units and against last year, and
 * the one a property manager is asked for on the phone. Derived from the same
 * lines as the table below, so the two can never disagree.
 */
/** One unit's tax for the year, and what that comes to per square foot. */
export interface UnitTaxRate {
  unitId: string;
  /** The suite the tax is assessed against. */
  label: string;
  sqFt: number | null;
  annual: number;
  psf: number | null;
}

export interface PerSqFt {
  /** Total occupancy area across every unit the tenant holds. */
  sqFt: number | null;
  /** Null until the budget allocation this reads from is built. */
  cams: number | null;
  /**
   * Tax per square foot, ONE ENTRY PER UNIT, largest first.
   *
   * Not blended into a single rate. Commercial space and a storage locker are
   * assessed on entirely different bases — 3,306 sq ft at $10.57 beside 326
   * at $0.79 — so an area-weighted average reports the shop a little cheap
   * and the locker at twelve times what it costs. The average is arithmetically
   * true and answers nothing anybody asks.
   *
   * A tenant holding one unit still sees one figure, which is the common case.
   * Units bearing no tax at all are absent rather than shown at zero.
   */
  taxes: UnitTaxRate[];
}

export interface TenantExpenseYear {
  year: number;
  lines: ExpenseLine[];
  perSqFt: PerSqFt;
  /**
   * True once the tenant has passed their natural breakpoint, which is what
   * puts the Percentage Rent row on the statement at all.
   */
  percentageRentApplies: boolean;
}

/** Sales for one tenant month, or null if they never reported it. */
const salesFor = (
  sales: TenantSalesMonth[],
  tenantId: string,
  year: number,
  month: number,
): number | null => {
  const found = sales.find(
    (entry) => entry.tenantId === tenantId && entry.year === year && entry.month === month,
  );
  return found ? Number(found.grossSales) || 0 : null;
};

/**
 * The month a tenant's cumulative sales first reach their breakpoint.
 *
 * Null when they never do. Percentage rent starts to accrue from this month,
 * and reaching the breakpoint is what makes the row appear at all.
 */
function breakpointCrossedIn(
  sales: TenantSalesMonth[],
  tenantId: string,
  year: number,
  breakpoint: number | null,
): number | null {
  if (breakpoint === null || breakpoint <= 0) return null;
  let running = 0;
  for (let month = 1; month <= 12; month += 1) {
    running += salesFor(sales, tenantId, year, month) ?? 0;
    if (running >= breakpoint) return month;
  }
  return null;
}

/** Whether a lease is running at any point in a given month. */
const runsInMonth = (
  lease: Occupancy,
  space: LeasedSpace,
  year: number,
  month: number,
): boolean => {
  const commenced = space.rentCommenceDate || lease.leaseCommenceDate;
  if (!commenced || commenced > monthEnd(year, month)) return false;
  const ended = occupancyEndDate(lease);
  return !ended || ended >= monthStart(year, month);
};

interface ExpenseInputs {
  tenant: Tenant;
  year: number;
  unitById: (unitId: string) => Unit | undefined;
  configuration: TospConfiguration;
  taxRolls: TaxRollRecord[];
  sales: TenantSalesMonth[];
  /**
   * This tenancy twelve CAM figures for the year, from the budget.
   *
   * Supplied rather than computed: CAM is the expense allocation, and this
   * calculator knows nothing about budgets. Omitted where there is no budget
   * for the year, and the row is then absent rather than empty.
   */
  cams?: number[];
}

// The year's statement, line by line.
//
// Charges follow the LEASE, not the calendar: a space whose rent commences in
// July is charged from July, and one whose lease ended in March stops there.
// Part months are charged whole — the lease states a monthly rent, and the
// property manager pro-rates the odd one by hand.
export function tenantExpenseYear({
  tenant,
  year,
  unitById,
  configuration,
  taxRolls,
  sales,
  cams,
}: ExpenseInputs): TenantExpenseYear {
  const leases = tenant.occupancies || [];

  const baseRent = emptyMonths();
  const storageRent = emptyMonths();
  const propertyTaxes = emptyMonths();
  const bmvaBase = emptyMonths();
  const bmvaRoyaltyLine = emptyMonths();
  const percentageRent = emptyMonths();

  const add = (line: (number | null)[], month: number, amount: number) => {
    line[month - 1] = (line[month - 1] ?? 0) + amount;
  };

  // ── Rent, and the BMVA fee that follows the same units ──────────────────
  for (const lease of leases) {
    for (const space of lease.leasedSpaces || []) {
      const unit = space.unitId ? unitById(space.unitId) : undefined;
      const escalation = spaceEscalation(lease, space);
      const commenced = space.rentCommenceDate || lease.leaseCommenceDate;
      const storage = STORAGE_SPACE.test(unit?.spaceType || "");

      for (let month = 1; month <= 12; month += 1) {
        if (!runsInMonth(lease, space, year, month)) continue;

        // The rate in force on the first day the lease is running this month
        // — which is the commencement date itself in the month it starts.
        const start = monthStart(year, month);
        const psf = rentPsfOn(escalation, commenced > start ? commenced : start);
        if (psf !== null && unit?.occupancySqFt != null) {
          add(storage ? storageRent : baseRent, month, (unit.occupancySqFt * psf) / 12);
        }

        if (unit) {
          // Exemptions are handled inside bmvaMonthlyFee, so an exempt unit
          // contributes a real zero rather than being skipped.
          //
          // Pro-rated to the day, like the taxes below and unlike the rent
          // above: the dues follow the unit, so a takeover splits the month it
          // falls in between the outgoing and incoming tenancies.
          add(
            bmvaBase,
            month,
            bmvaMonthlyFee(unit, configuration) *
              occupancyDayShare(lease, space, year, month),
          );
        }
      }
    }
  }

  // ── Property taxes, per roll share the tenant's units hold ──────────────
  //
  // Budgeted on the roll, so the unit's charge is the sum of the shares
  // assigned to it. A year with no budget entered stays blank rather than
  // reading as nil tax.
  const chargedUnits = new Map<string, Occupancy>();
  for (const lease of leases) {
    for (const space of lease.leasedSpaces || []) {
      if (space.unitId && !chargedUnits.has(space.unitId)) chargedUnits.set(space.unitId, lease);
    }
  }

  // Each taxed unit's own rate, kept apart rather than pooled — see the
  // UnitTaxRate note above for why an average of these misleads.
  const taxRates: UnitTaxRate[] = [];

  for (const [unitId, lease] of chargedUnits) {
    const annual = rollPartsForUnit(taxRolls, unitId).reduce((sum, { roll, part }) => {
      const budget = (roll.budgets || []).find(
        (entry) => entry.year === year && entry.partId === part.id,
      );
      return budget ? sum + budget.amount : sum;
    }, 0);
    // No roll assigned, or none budgeted for this year: no tax, and no rate to
    // report for it either.
    if (annual === 0) continue;

    const unit = unitById(unitId);
    const area = unit?.occupancySqFt ?? null;
    taxRates.push({
      unitId,
      label: unit?.suiteNumber || "Unit",
      sqFt: area,
      annual,
      psf: area ? annual / area : null,
    });

    // Pro-rated to the day, unlike the rent above. Taxes follow occupancy: they
    // switch to the incoming tenant on the takeover date, so the month a unit
    // changes hands divides between the two tenancies by the days each held it.
    // Charging the boundary month whole to both would bill the same tax twice.
    for (let month = 1; month <= 12; month += 1) {
      const space = (lease.leasedSpaces || []).find((s) => s.unitId === unitId);
      if (!space) continue;
      const share = occupancyDayShare(lease, space, year, month);
      if (share > 0) add(propertyTaxes, month, (annual / 12) * share);
    }
  }

  // ── The two lines that ride on sales ────────────────────────────────────
  const royaltyRate = leases
    .map((lease) => bmvaRoyalty(configuration, lease))
    .find((rate) => rate !== null && rate !== undefined);

  if (royaltyRate) {
    for (let month = 1; month <= 12; month += 1) {
      const reported = salesFor(sales, tenant.id, year, month);
      if (reported !== null) add(bmvaRoyaltyLine, month, reported * (royaltyRate / 100));
    }
  }

  // Percentage rent is charged a month in arrears: a month's takings aren't
  // known until it has closed and been reported, by which time that month has
  // already been invoiced. So August's sales appear on September's statement —
  // and December's on the following January, which is why the previous year is
  // read here as well as this one.
  const breakpointRate = leases
    .map((lease) => lease.percentageRentPayment)
    .find((rate) => rate !== null && rate !== undefined);

  const thisYearsBreakpoint = tenantBreakpointDetail(
    tenant,
    unitById,
    configuration,
    year,
  ).total;
  const crossedThisYear = breakpointCrossedIn(sales, tenant.id, year, thisYearsBreakpoint);

  const lastYearsBreakpoint = tenantBreakpointDetail(
    tenant,
    unitById,
    configuration,
    year - 1,
  ).total;
  const crossedLastYear = breakpointCrossedIn(sales, tenant.id, year - 1, lastYearsBreakpoint);

  if (breakpointRate) {
    for (let month = 1; month <= 12; month += 1) {
      // The month being charged for: the one before this statement line.
      const fromYear = month === 1 ? year - 1 : year;
      const fromMonth = month === 1 ? 12 : month - 1;
      const crossed = fromYear === year ? crossedThisYear : crossedLastYear;
      if (crossed === null || fromMonth < crossed) continue;

      const reported = salesFor(sales, tenant.id, fromYear, fromMonth);
      if (reported !== null) {
        percentageRent[month - 1] = reported * (breakpointRate / 100);
      }
    }
  }

  const percentageRentApplies = percentageRent.some((value) => value !== null);

  const lines: ExpenseLine[] = [
    { key: "baseRent", label: "Base Rent", months: baseRent, total: null },
    // Only where a storage space is actually held. Most tenants have none, and
    // a permanent row of dashes reads as a charge nobody can account for
    // rather than as one that does not apply. Base Rent stays whatever it
    // holds — a tenant with no rent at all is a fault worth seeing, not a
    // line worth hiding.
    ...(storageRent.some((amount) => amount !== null)
      ? [{ key: "storageRent", label: "Storage Rent", months: storageRent, total: null }]
      : []),
    { key: "propertyTaxes", label: "Property Taxes", months: propertyTaxes, total: null },
    // CAM IS NOT THIS CALCULATOR'S TO WORK OUT. It is the expense allocation,
    // which belongs to a budget — so the caller supplies it, from the budget
    // for this year, and the row appears only when they can.
    //
    // A published year gets it from the frozen statement instead; see
    // expenseYearFromSnapshot. A year with no budget at all has no CAM figure
    // in existence, and shows no row — which is what the old dashed row and
    // its "not yet calculated" caption were really saying, at more length and
    // about the software rather than the tenancy.
    ...(cams
      ? [{ key: "cams", label: "CAMS", months: [...cams], total: null } as ExpenseLine]
      : []),
    { key: "bmvaBase", label: "BMVA Base Fee", months: bmvaBase, total: null },
    { key: "bmvaRoyalty", label: "BMVA Royalty", months: bmvaRoyaltyLine, total: null },
  ];

  // Hidden entirely until the tenant reaches their breakpoint — a row of
  // dashes would suggest percentage rent was owed and unknown, when in fact
  // none is owed at all.
  if (percentageRentApplies) {
    lines.push({
      key: "percentageRent",
      label: "Percentage Rent",
      months: percentageRent,
      total: null,
    });
  }

  const hst = emptyMonths();
  const total = emptyMonths();
  for (let month = 1; month <= 12; month += 1) {
    const charged = lines
      .map((line) => line.months[month - 1])
      .filter((value): value is number => value !== null);
    if (charged.length === 0) continue;
    const subtotal = charged.reduce((sum, value) => sum + value, 0);
    hst[month - 1] = subtotal * HST_RATE;
    total[month - 1] = subtotal + (hst[month - 1] as number);
  }

  lines.push({ key: "hst", label: "HST", months: hst, total: null });
  lines.push({ key: "total", label: "Total", months: total, total: null, emphasis: true });

  for (const line of lines) {
    const known = line.months.filter((value): value is number => value !== null);
    line.total = known.length ? known.reduce((sum, value) => sum + value, 0) : null;
  }

  // Area is counted once per UNIT, not once per leased space: two spaces
  // pointing at one unit are one floor, and adding it twice would halve every
  // rate below.
  let sqFt: number | null = null;
  for (const unitId of chargedUnits.keys()) {
    const area = unitById(unitId)?.occupancySqFt;
    if (area != null) sqFt = (sqFt ?? 0) + area;
  }

  const yearTotal = (key: string): number | null =>
    lines.find((line) => line.key === key)?.total ?? null;

  const perSqFtOf = (key: string, over: number | null): number | null => {
    const total = yearTotal(key);
    return total === null || !over ? null : total / over;
  };

  return {
    year,
    lines,
    perSqFt: {
      sqFt,
      cams: perSqFtOf("cams", sqFt),
      // Largest first, so the premises lead and the lockers follow.
      taxes: [...taxRates].sort((a, b) => (b.sqFt ?? 0) - (a.sqFt ?? 0)),
    },
    percentageRentApplies,
  };
}

/**
 * The rates a statement prints above its table, for one tenancy.
 *
 * NOT FROM THE BUDGET, deliberately. A unit's area and what it is assessed for
 * are facts about the premises, so they come from the unit and the tax roll
 * whether the year is published or still being worked up — which is also what
 * lets a published statement freeze them at the moment of publication.
 *
 * The charges themselves are a different matter entirely: those come from the
 * budget, which is why this returns rates and nothing else.
 *
 * Read through `unitBudgetTotal`, the same figure the revenue budget's tax line
 * is built from, so the rate quoted here and the tax charged below it are the
 * same assessment. Where a tenancy carries part of a unit it does not hold —
 * an unlet unit's taxes, recovered from the let units of its building — the
 * charge exceeds these rates, and the statement's own line is what says so.
 */
export function statementRatesFor(
  tenant: Tenant,
  unitById: (unitId: string) => Unit | undefined,
  taxRolls: TaxRollRecord[],
  year: number,
): { sqFt: number | null; taxes: UnitTaxRate[] } {
  // Once per UNIT, not once per leased space: two spaces pointing at one unit
  // are one floor, and counting it twice would halve every rate.
  const unitIds: string[] = [];
  for (const occupancy of tenant.occupancies || []) {
    for (const space of occupancy.leasedSpaces || []) {
      if (space.unitId && !unitIds.includes(space.unitId)) unitIds.push(space.unitId);
    }
  }

  let sqFt: number | null = null;
  const taxes: UnitTaxRate[] = [];
  for (const unitId of unitIds) {
    const unit = unitById(unitId);
    const area = unit?.occupancySqFt ?? null;
    if (area != null) sqFt = (sqFt ?? 0) + area;

    // No roll assigned, or nothing budgeted for the year: no rate to report.
    // A unit bearing no tax is absent rather than shown at zero.
    const annual = unitBudgetTotal(taxRolls, unitId, year) ?? 0;
    if (annual === 0) continue;
    taxes.push({
      unitId,
      label: unit?.suiteNumber || "Unit",
      sqFt: area,
      annual,
      psf: area ? annual / area : null,
    });
  }

  // Largest first, so the premises lead and the lockers follow.
  return { sqFt, taxes: taxes.sort((a, b) => (b.sqFt ?? 0) - (a.sqFt ?? 0)) };
}

/**
 * A frozen statement, in the shape the table already renders.
 *
 * So the tenant's screen, the admin's preview, GROC and the estoppels all read
 * ONE shape whether the figures came from a published snapshot or from the
 * live calculator. Nothing downstream needs to know which, which is what stops
 * the four views drifting apart.
 *
 * Nothing is pending on a published statement: the whole point of publishing
 * is that every line has a figure. A line the tenancy carries nothing on was
 * left out when the snapshot was built, rather than being shown at nil.
 */
export function expenseYearFromSnapshot(
  snapshot: BudgetStatementSnapshot,
  year: number,
): TenantExpenseYear {
  return {
    year,
    lines: snapshot.lines.map((line) => ({
      key: line.key,
      label: line.label,
      months: [...line.months],
      total: line.total,
      ...(line.emphasis ? { emphasis: true } : {}),
    })),
    perSqFt: {
      sqFt: snapshot.perSqFt.sqFt,
      cams: snapshot.perSqFt.cams,
      taxes: snapshot.perSqFt.taxes.map((rate) => ({ ...rate })),
    },
    percentageRentApplies: snapshot.lines.some((line) => line.key === "percentageRent"),
  };
}

/**
 * Every tenancy's twelve CAM figures for one budget's year.
 *
 * COMPUTED ONCE PER BUDGET, not once per tenancy. The recovery divides eleven
 * categories across twelve months and ninety-odd units; calling it per tenant
 * would be fifty-odd times the work, and that is precisely what once blocked
 * the first frames of the Overview and broke its reveal animation.
 *
 * Returns null where the budget has no categorised expenses to divide, so the
 * caller can tell "no CAM yet" from "CAM of nil".
 */
export function camMonthsByTenant(
  budget: Pick<
    BudgetYear,
    "year" | "lines" | "splits" | "expenseOverrides" | "camOccupancy"
  >,
  units: Unit[],
  tenants: Tenant[],
): Map<string, number[]> | null {
  const recovery = calculateCamRecovery(
    withManagementFee(
      effectiveLines(budget.lines, budget.expenseOverrides),
      budget.splits,
    ),
    budget.splits,
    units,
    tenants,
    budget.year,
    budget.camOccupancy,
  );
  if (recovery.rows.length === 0) return null;

  const byTenant = new Map<string, number[]>();
  recovery.rows.forEach((row) => byTenant.set(row.id, row.months));
  return byTenant;
}

/**
 * The statement a budget PUBLISHED for this tenancy and year, or null.
 *
 * Broadcast is not consulted here, deliberately: it decides whether a tenant
 * may SEE the year, which the backend enforces, while this answers which
 * figures are authoritative. An admin reading a published year — on the
 * preview, in GROC, on an estoppel — must see the published figures whether
 * the year has been released to tenants or not.
 */
export function publishedStatement(
  budgets: Pick<BudgetYear, "year" | "history" | "publishedSnapshot">[],
  tenantId: string,
  year: number,
): BudgetStatementSnapshot | null {
  const budget = budgets.find(
    (candidate) => candidate.year === year && !!candidate.history?.published,
  );
  return (
    budget?.publishedSnapshot?.statements.find((row) => row.tenantId === tenantId) || null
  );
}

